import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Flame, CalendarCheck, PenSquare, BookOpen, Clock, Lock, ImageDown,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { renderStreakCard, shareStreakCard } from "../lib/streakCard";

const pct = (n, of) => Math.min(100, Math.round((Math.min(n, of) / of) * 100));

function buildGroups({ stats, quarters, years }) {
  const streak = stats?.streak_weeks ?? 0;
  const seen = stats?.streak_milestones_seen || [];
  const best = Math.max(streak, ...seen, 0);
  const held = stats?.completed_meetings ?? 0;
  const signed = stats?.completed_action_items ?? 0;
  const compiledQ = quarters.filter((q) => q.compiled_at).length;
  const compiledY = years.filter((y) => y.compiled_at).length;
  const scheduledHeld = stats?.scheduled_sessions_held ?? 0;
  const onTime = stats?.on_time_rate;

  return [
    {
      key: "streak",
      title: "Weeks in the chair",
      icon: Flame,
      badges: [4, 12, 26, 52].map((m) => ({
        id: `streak-${m}`,
        name: m === 52 ? "A year in the chair" : m === 26 ? "Half a year unbroken" : m === 12 ? "A full quarter held" : "Four weeks running",
        note: `${m} consecutive weeks with a session held`,
        earned: best >= m,
        progress: pct(best, m),
        progressLabel: `${Math.min(best, m)} of ${m} weeks`,
        card: m,
      })),
    },
    {
      key: "sessions",
      title: "Sessions on the record",
      icon: CalendarCheck,
      badges: [1, 10, 25, 50, 100].map((m) => ({
        id: `sessions-${m}`,
        name: m === 1 ? "First session" : `${m} sessions held`,
        note: m === 1 ? "You called yourself to order" : `${m} sessions closed out`,
        earned: held >= m,
        progress: pct(held, m),
        progressLabel: `${Math.min(held, m)} of ${m}`,
      })),
    },
    {
      key: "follow",
      title: "Follow-through",
      icon: PenSquare,
      badges: [
        ...[10, 50, 100].map((m) => ({
          id: `signed-${m}`,
          name: `${m} sign-offs`,
          note: `${m} action items completed and dated`,
          earned: signed >= m,
          progress: pct(signed, m),
          progressLabel: `${Math.min(signed, m)} of ${m}`,
        })),
        {
          id: "punctual",
          name: "Punctual chair",
          note: "90%+ of booked sessions held on time (min. 8)",
          earned: scheduledHeld >= 8 && (onTime ?? 0) >= 90,
          progress: scheduledHeld < 8 ? pct(scheduledHeld, 8) : pct(onTime ?? 0, 90),
          progressLabel: scheduledHeld < 8 ? `${scheduledHeld} of 8 booked` : `${onTime ?? 0}% on time`,
        },
      ],
    },
    {
      key: "books",
      title: "Board Books",
      icon: BookOpen,
      badges: [
        {
          id: "quarter-1",
          name: "First board book",
          note: "One quarter compiled into a review",
          earned: compiledQ >= 1,
          progress: pct(compiledQ, 1),
          progressLabel: `${compiledQ} of 1 quarter`,
        },
        {
          id: "quarter-4",
          name: "Four quarters filed",
          note: "A full year of compiled quarters",
          earned: compiledQ >= 4,
          progress: pct(compiledQ, 4),
          progressLabel: `${Math.min(compiledQ, 4)} of 4 quarters`,
        },
        {
          id: "year-1",
          name: "Annual review",
          note: "A year compiled into one book",
          earned: compiledY >= 1,
          progress: pct(compiledY, 1),
          progressLabel: `${compiledY} of 1 year`,
        },
      ],
    },
  ];
}

function Badge({ badge, onCard, busy, GroupIcon }) {
  const Icon = badge.earned ? GroupIcon : Lock;
  return (
    <div
      className={`rounded-md p-5 flex flex-col transition-colors ${
        badge.earned
          ? "bg-[#121212] border border-[#c89f45]/40"
          : "bg-[#0e0e0e] border border-[#1e1e1e]"
      }`}
      data-testid={`badge-${badge.id}`}
    >
      <div
        className={`h-12 w-12 rounded-full flex items-center justify-center border ${
          badge.earned ? "border-[#c89f45]/70 bg-[#c89f45]/10" : "border-[#242424]"
        }`}
      >
        <Icon size={18} className={badge.earned ? "text-[#c89f45]" : "text-[#4a4a4a]"} strokeWidth={1.5} />
      </div>
      <p className={`font-serif text-lg mt-4 leading-snug ${badge.earned ? "text-[#fafafa]" : "text-[#7a7a7a]"}`}>
        {badge.name}
      </p>
      <p className="text-sm text-[#8a8a8a] mt-1.5 leading-relaxed">{badge.note}</p>

      {badge.earned ? (
        badge.card ? (
          <button
            type="button"
            onClick={() => onCard(badge.card)}
            disabled={busy}
            className="mt-4 inline-flex items-center gap-1.5 self-start min-h-[40px] text-xs text-[#c89f45] hover:underline disabled:opacity-60"
            data-testid={`badge-card-${badge.id}`}
          >
            <ImageDown size={13} /> {busy ? "Making the card…" : "Share card"}
          </button>
        ) : (
          <p className="mt-4 text-[10px] font-mono uppercase tracking-widest text-[#c89f45]">Earned</p>
        )
      ) : (
        <div className="mt-4">
          <div className="h-1 rounded bg-[#1c1c1c] overflow-hidden">
            <div className="h-full bg-[#c89f45]/60" style={{ width: `${badge.progress}%` }} />
          </div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#7a7a7a] mt-2">
            {badge.progressLabel}
          </p>
        </div>
      )}
    </div>
  );
}

/** The wall — every badge the chair has earned, and the ones still in reach. */
export default function Milestones() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [quarters, setQuarters] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/stats").then((r) => setStats(r.data)).catch(() => {}),
      api.get("/quarter-reviews").then((r) => setQuarters(r.data.quarters || [])).catch(() => {}),
      api.get("/year-reviews").then((r) => setYears(r.data.years || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const saveCard = async (weeks) => {
    setBusy(true);
    try {
      const blob = await renderStreakCard({
        weeks,
        chairName: user?.name || user?.email,
        onTimeRate: (stats?.scheduled_sessions_held ?? 0) >= 2 ? stats?.on_time_rate : null,
        mintedLabel: new Date().toLocaleDateString(undefined, { month: "short", year: "2-digit" }),
      });
      const how = await shareStreakCard(blob, weeks);
      if (how === "downloaded") toast.success("Card saved to your downloads.");
    } catch {
      toast.error("Could not build the card.");
    } finally {
      setBusy(false);
    }
  };

  const groups = buildGroups({ stats, quarters, years });
  const all = groups.flatMap((g) => g.badges);
  const earned = all.filter((b) => b.earned).length;

  return (
    <div className="space-y-10" data-testid="milestones-page">
      <div>
        <p className="label-eyebrow">The wall</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-2 leading-tight">
          Everything you've earned.
        </h1>
        <p className="text-[#8a8a8a] mt-3 max-w-xl">
          Weeks held, sessions closed, promises signed off and books filed — the year's proof on one screen.
        </p>
        <p className="mt-5 font-mono text-xs uppercase tracking-widest text-[#c89f45]" data-testid="milestones-count">
          {loading ? "Counting…" : `${earned} of ${all.length} badges earned`}
        </p>
      </div>

      {groups.map((g) => {
        const Icon = g.icon;
        return (
          <section key={g.key} data-testid={`milestone-group-${g.key}`}>
            <div className="flex items-center gap-2">
              <Icon size={15} className="text-[#c89f45]" strokeWidth={1.5} />
              <h2 className="font-serif text-xl">{g.title}</h2>
              <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#7a7a7a]">
                {g.badges.filter((b) => b.earned).length}/{g.badges.length}
              </span>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {g.badges.map((b) => (
                <Badge key={b.id} badge={b} onCard={saveCard} busy={busy} GroupIcon={Icon} />
              ))}
            </div>
          </section>
        );
      })}

      <div className="surface rounded-md p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <p className="font-serif text-lg">Next badge is one session away.</p>
          <p className="text-sm text-[#8a8a8a] mt-1">
            Every session held moves the wall. Book the next one now.
          </p>
        </div>
        <Link
          to="/meetings/new"
          className="h-11 px-5 rounded-md bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] text-sm font-medium inline-flex items-center justify-center transition-colors"
          data-testid="milestones-cta"
        >
          Call a session
        </Link>
      </div>

      <p className="flex items-center gap-2 text-xs text-[#7a7a7a]">
        <Clock size={12} /> Streak badges also live on a public page you can share from the streak card.
      </p>
    </div>
  );
}
